import { ref, readonly } from 'vue';

/**
 * Global server status tracking composable
 * Tracks when the application encounters 5xx server errors
 */

// Global reactive state - shared across all instances
const hasServerError = ref(false);
const lastServerError = ref<Date | null>(null);
const errorDetails = ref<string>('');

export function useServerStatus() {
    /**
     * Report a server error (5xx status codes)
     * @param statusCode - The HTTP status code
     * @param errorMessage - Optional error message details
     */
    const reportServerError = (statusCode: number, errorMessage?: string): void => {
        if (statusCode >= 500 && statusCode < 600) {
            hasServerError.value = true;
            lastServerError.value = new Date();
            errorDetails.value = errorMessage || `Server error ${statusCode}`;
            
            console.warn('🔴 Server error detected:', {
                statusCode,
                message: errorMessage,
                timestamp: lastServerError.value
            });
        }
    };

    /**
     * Clear the server error state
     * Called on successful API responses, logout, or refresh
     */
    const clearServerError = (): void => {
        if (hasServerError.value) {
            console.info('✅ Server error status cleared');
        }
        
        hasServerError.value = false;
        lastServerError.value = null;
        errorDetails.value = '';
    };

    /**
     * Report a successful API response
     * This automatically clears any existing server error state
     */
    const reportSuccess = (): void => {
        if (hasServerError.value) {
            clearServerError();
        }
    };

    return {
        // State
        hasServerError: readonly(hasServerError),
        lastServerError: readonly(lastServerError),
        errorDetails: readonly(errorDetails),
        
        // Methods
        reportServerError,
        clearServerError,
        reportSuccess
    };
}
